import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { API_URL } from '@/lib/apiUrl';
import { getCsrfToken, isMutatingMethod } from '@/lib/csrf';

if (!API_URL) {
  console.warn('[api/client] VITE_API_URL is not set. API calls will fail.');
}

let isRefreshing = false;
let refreshSubscribers: Array<() => void> = [];

function subscribeTokenRefresh(cb: () => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed() {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
}

/**
 * Rotate the session cookie using the httpOnly refresh cookie. Exported so non-axios
 * callers (the raw-fetch upload helpers, which bypass the response interceptor below)
 * can refresh + retry on a 401 too — otherwise an expired 15-minute access token fails
 * an upload with "expired token". Returns true on success.
 */
export async function refreshSession(): Promise<boolean> {
  try {
    await axios.post(
      `${API_URL}/auth/refresh/cookie`,
      {},
      { withCredentials: true, headers: { 'X-CSRF-Token': getCsrfToken(false) ?? '' } },
    );
    return true;
  } catch {
    return false;
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  // Send the httpOnly session/refresh cookies on every request. Cookie auth (Wave 2.4)
  // replaces the old Authorization: Bearer header — there is no JS-readable token.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach tenant slug + CSRF double-submit token.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { tenantSlug, activeSiteId, impersonating } = useAuthStore.getState();
    if (tenantSlug && config.headers && !config.headers['X-Tenant-Slug']) {
      config.headers['X-Tenant-Slug'] = tenantSlug;
    }
    // Multi-site: tell the gateway which site is being edited. Absent → gateway uses the
    // tenant's primary site, so single-site behavior is unchanged.
    if (activeSiteId && config.headers && !config.headers['X-Site-Id']) {
      config.headers['X-Site-Id'] = activeSiteId;
    }
    // Double-submit CSRF on mutations: echo the JS-readable CSRF cookie into a header
    // the gateway compares against the cookie. Select impersonation vs session token.
    if (isMutatingMethod(config.method) && config.headers) {
      const csrf = getCsrfToken(impersonating);
      if (csrf) config.headers['X-CSRF-Token'] = csrf;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — on 401, refresh the session cookie once, then retry.
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => resolve(apiClient(originalRequest)));
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Rotate the session cookie via the httpOnly refresh cookie, then replay.
        const refreshed = await refreshSession();
        if (!refreshed) throw new Error('session refresh failed');
        onRefreshed();
        return apiClient(originalRequest);
      } catch {
        // Refresh failed — drop auth (and tell other tabs) and bounce to login.
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // Surface the SERVER's explanation instead of axios's "Request failed with status code 500".
    // Every caller reads `e.message`, so without this the one useful sentence — "the AI provider
    // is rate-limiting us, wait a few seconds" — is replaced by a status code, and the chat
    // appends its own guess ("try rephrasing") which is often the wrong advice entirely.
    const body = error.response?.data as
      | { error?: unknown; code?: unknown; detail?: unknown; route?: unknown }
      | undefined;
    if (typeof body?.error === 'string' && body.error.trim()) {
      // Append the cause when the server sent one. It only does so for the classes its own
      // message cannot identify — an unclassified failure, a request we built wrong, a missing
      // model — and those are exactly the ones where "The AI request failed" ended the
      // investigation, because the real reason lived only in a server log nobody watching the
      // chat (or a CI run, or a bug report) can reach.
      const detail = typeof body.detail === 'string' ? body.detail.trim() : '';
      const route = typeof body.route === 'string' ? body.route.trim() : '';
      const where = route ? ` [${route}]` : '';
      error.message = detail ? `${body.error}${where} — ${detail}` : `${body.error}${where}`;
      if (typeof body.code === 'string') (error as { serverCode?: string }).serverCode = body.code;
    }
    return Promise.reject(error);
  }
);

// Helper to make typed API calls
export async function apiGet<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<{ data: T }>(path, { params });
  return response.data.data;
}

export async function apiPost<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<{ data: T }>(path, body, config);
  return response.data.data;
}

export async function apiPut<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.put<{ data: T }>(path, body, config);
  return response.data.data;
}

export async function apiPatch<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.patch<{ data: T }>(path, body, config);
  return response.data.data;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const response = await apiClient.delete<{ data: T }>(path);
  return response.data.data;
}

/**
 * Multipart file upload. Pass a FormData — axios sets the correct
 * Content-Type boundary automatically. Do NOT set Content-Type manually.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  onProgress?: (pct: number) => void,
): Promise<T> {
  const response = await apiClient.post<{ data: T }>(path, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
      : undefined,
  });
  return response.data.data;
}
